import React from 'react'
import { t } from 'ttag'
import { UploadStats } from '../../services/ActivityUploadService'

interface OfflineStatusBadgeProps {
  uploadStats: UploadStats
  onRetry?: () => void
  onClear?: () => void
  isLoading?: boolean
}

/**
 * Badge component showing offline upload status and controls
 * Provides visual feedback for queued activities and retry functionality
 */
const OfflineStatusBadge: React.FC<OfflineStatusBadgeProps> = ({
  uploadStats,
  onRetry,
  onClear,
  isLoading = false,
}) => {
  if (uploadStats.queuedCount === 0 && uploadStats.isOnline) {
    return null // Nothing to show when online with no queue
  }

  const getBadgeColor = () => {
    if (!uploadStats.isOnline) return '#f44336' // Red for offline
    if (uploadStats.isProcessing) return '#2196f3' // Blue for processing
    return '#ff9800' // Orange for queued items
  }

  const getBadgeIcon = () => {
    if (!uploadStats.isOnline) return '📴'
    if (uploadStats.isProcessing) return '🔄'
    return '📤'
  }

  const getBadgeText = () => {
    if (!uploadStats.isOnline) {
      return uploadStats.queuedCount > 0 ? t`Offline (${uploadStats.queuedCount} queued)` : t`Offline`
    }
    if (uploadStats.isProcessing) {
      return t`Uploading...`
    }
    return t`${uploadStats.queuedCount} pending`
  }

  const getTooltipText = () => {
    if (!uploadStats.isOnline) {
      return uploadStats.queuedCount > 0
        ? t`Device is offline. ${uploadStats.queuedCount} activities will upload when online.`
        : t`Device is offline. Activities will be saved and uploaded when online.`
    }
    if (uploadStats.isProcessing) {
      return t`Uploading queued activities...`
    }
    return t`${uploadStats.queuedCount} activities waiting to upload. Click retry to upload now.`
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        borderRadius: '12px',
        backgroundColor: `${getBadgeColor()}15`, // 15% opacity background
        border: `1px solid ${getBadgeColor()}40`, // 40% opacity border
        fontSize: '11px',
        fontWeight: '500',
        color: getBadgeColor(),
        maxWidth: '200px',
      }}
      title={getTooltipText()}
    >
      <span>{getBadgeIcon()}</span>
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {getBadgeText()}
      </span>

      {/* Retry button - only show when online with queued items */}
      {uploadStats.isOnline && uploadStats.queuedCount > 0 && !uploadStats.isProcessing && onRetry && (
        <button
          onClick={onRetry}
          disabled={isLoading}
          style={{
            background: 'none',
            border: 'none',
            color: getBadgeColor(),
            cursor: isLoading ? 'wait' : 'pointer',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '10px',
            opacity: isLoading ? 0.6 : 1,
          }}
          title={t`Retry uploading now`}
        >
          ↻
        </button>
      )}

      {/* Clear button - only show when there are queued items (admin function) */}
      {uploadStats.queuedCount > 0 && onClear && (
        <button
          onClick={onClear}
          disabled={isLoading || uploadStats.isProcessing}
          style={{
            background: 'none',
            border: 'none',
            color: getBadgeColor(),
            cursor: isLoading || uploadStats.isProcessing ? 'not-allowed' : 'pointer',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '10px',
            opacity: isLoading || uploadStats.isProcessing ? 0.4 : 0.7,
          }}
          title={t`Clear pending uploads`}
        >
          ✕
        </button>
      )}
    </div>
  )
}

export default OfflineStatusBadge
